import React, { useCallback, useMemo } from "react"
import styled from "styled-components"
import { State } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { EMPTY_PERSON } from "../../globalStates/ExhibitorDataState"
import { addValidatorsPerson } from "../../globalStates/ExhibitorValidation"
import { useLanguageState } from "../../globalStates/LanguageState"
import { addElement, removeElementById } from "../../globalStates/utils"
import { newId } from "../../utils/Id"
import { repositionItem } from "../../utils/Arrays"
import Button from "../../components/Button"
import TeamSection, { PersonFunctionType } from "../team/PersonSection"
import { Person } from "../../globalStates/ExhibitorDataTypes"

const ButtonsBar = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`

interface PersonsSectionProps {
    personsStates: State<Person[], Validation>
    type: PersonFunctionType
}

function sortPersons(personStates: State<Person[], Validation>): State<Person, Validation>[] {
    const persons = personStates.map((personState) => personState)
    return persons.sort((p1, p2) => p1.orderPerson.get() - p2.orderPerson.get())
}

const PersonsSection: React.FC<PersonsSectionProps> = ({ personsStates, type }) => {
    const branding = useLanguageState().getStrings()
    const orderedPersonsStates = useMemo(() => sortPersons(personsStates), [personsStates])

    const canAddPerson = useMemo(() => {
        let valid = true
        personsStates.forEach((personState) => {
            const firstName = personState.firstName.get()
            const lastName = personState.lastName.get()
            valid = valid && !!firstName && firstName.length >= 0 && !!lastName && lastName.length >= 0
        })
        return valid
    }, [personsStates])

    const onAddPerson = useCallback(() => {
        addElement<Person>(personsStates, { ...EMPTY_PERSON, id: newId().toString() })
        addValidatorsPerson(personsStates[personsStates.length - 1])
    }, [personsStates])

    const onRemovePerson = useCallback(
        (personId: string) => {
            removeElementById<Person>(personsStates, personId)
        },
        [personsStates]
    )

    const onOrderChanged = useCallback(
        (personId: string, newIndex: number) => {
            const oldIndex = orderedPersonsStates.findIndex((personState) => personState.id.get() === personId)
            const repositionedPersonStates = repositionItem(
                orderedPersonsStates,
                orderedPersonsStates[oldIndex],
                oldIndex,
                newIndex
            )
            repositionedPersonStates.forEach((person, index) => {
                if (person.orderPerson.get() !== index + 1) person.orderPerson.set(index + 1)
            })
        },
        [orderedPersonsStates]
    )

    return (
        <div>
            {orderedPersonsStates?.map((personState, index) => (
                <TeamSection
                    key={personState.id.get()}
                    personState={personState}
                    index={index}
                    onOrderChanged={onOrderChanged}
                    onClickRemove={onRemovePerson}
                    type={type}
                />
            ))}
            <ButtonsBar>
                <Button
                    id="addPerson"
                    caption={branding.configuration.teamPage.btnAddSPerson}
                    inverse
                    onClick={onAddPerson}
                    disabled={!canAddPerson}
                />
            </ButtonsBar>
        </div>
    )
}
export default PersonsSection
